import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { PageLink } from 'src/_metronic/layout/core';
import { getBreadcrumbLabel } from '../routing/breadcrumbConfig';

/**
 * Automatically generates breadcrumbs from current URL
 * 
 * This hook parses the current route and builds a breadcrumb trail
 * by looking up labels in the breadcrumbConfig.
 * 
 * Example:
 * URL: /administration/department
 * Returns: [
 *   { title: 'Administration', path: '/administration', isActive: false },
 *   { title: 'Department', path: '/administration/department', isActive: true }
 * ]
 * 
 * @returns Array of breadcrumb links
 */
export function useBreadcrumbs(): PageLink[] {
  const location = useLocation();
  
  const breadcrumbs = useMemo(() => {
    const pathname = location.pathname;
    
    // Remove trailing slash
    const cleanPath = pathname.replace(/\/$/, '');
    
    // Remove basename (from PUBLIC_URL) if present
    const pathWithoutBase = cleanPath.replace(new RegExp(`^${process.env.PUBLIC_URL}`), '');
    
    // Special case: For Selection Group, show only "Selection Group"
    if (pathWithoutBase === '/design/lib-profile/fields-lookup') {
      return [
        {
          title: 'Selection Group',
          path: pathWithoutBase,
          isActive: true,
        },
      ];
    }
    
    // Special case: For Document Type, show only "Document Type"
    if (pathWithoutBase === '/design/lib-profile/document-type') {
      return [
        {
          title: 'Document Type',
          path: pathWithoutBase,
          isActive: true,
        },
      ];
    }
    
    // Special case: For Process Design listing, show only "Process Design"
    if (pathWithoutBase === '/design/process/process-list') {
      return [
        {
          title: 'Process Design',
          path: pathWithoutBase,
          isActive: true,
        },
      ];
    }
    
    // Special case: For Library Design listing, show only "Library Design"
    if (pathWithoutBase === '/design/lib-profile/record-profile-design') {
      return [
        {
          title: 'Library Design',
          path: pathWithoutBase,
          isActive: true,
        },
      ];
    }
    
    // Special case: For Administration routes, show only the specific page name
    if (pathWithoutBase === '/administration/department') {
      return [
        {
          title: 'Department',
          path: pathWithoutBase,
          isActive: true,
        },
      ];
    }
    
    if (pathWithoutBase === '/administration/position') {
      return [
        {
          title: 'Position',
          path: pathWithoutBase,
          isActive: true,
        },
      ];
    }
    
    if (pathWithoutBase === '/administration/user') {
      return [
        {
          title: 'User',
          path: pathWithoutBase,
          isActive: true,
        },
      ];
    }
    
    if (pathWithoutBase === '/administration/access') {
      return [
        {
          title: 'Access Permission',
          path: pathWithoutBase,
          isActive: true,
        },
      ];
    }
    
    if (pathWithoutBase === '/administration/audit') {
      return [
        {
          title: 'Audit Trail',
          path: pathWithoutBase,
          isActive: true,
        },
      ];
    }
    
    // Special case: For Library Access Setting Create, show "Library Access Setting / Add new"
    if (pathWithoutBase === '/design/lib-profile/library-access-setting/create') {
      return [
        {
          title: 'Library Access Setting',
          path: '/design/lib-profile/library-access-setting',
          isActive: false,
        },
        {
          title: 'Add new',
          path: pathWithoutBase,
          isActive: true,
        },
      ];
    }
    
    // Special case: For New Process, show "Process Design / Add new"
    if (pathWithoutBase === '/design/process/new-process') {
      return [
        {
          title: 'Process Designer',
          path: '/design/process/process-list',
          isActive: false,
        },
        {
          title: 'Add new',
          path: pathWithoutBase,
          isActive: true,
        },
      ];
    }
        // Special case: For Process Access Permission Create, show "Process Access Setting / Add new"
    if (pathWithoutBase === '/process/process-access/process-access-permission/create') {
      return [
        {
          title: 'Process Access Setting',
          path: '/process/process-access/process-access-permission',
          isActive: false,
        },
        {
          title: 'Add new',
          path: pathWithoutBase,
          isActive: true,
        },
      ];
    }
        // Special case: For New Process with ID (dynamic routes)
    if (pathWithoutBase.startsWith('/design/process/new-process/')) {
      const segments = pathWithoutBase.split('/');
      const id = segments[segments.length - 1];
      
      if (id === '1') {
        // New Process Design
        return [
          {
            title: 'Process Designer',
            path: '/design/process/process-list',
            isActive: false,
          },
          {
            title: 'Add new',
            path: pathWithoutBase,
            isActive: true,
          },
        ];
      } else {
        // Edit Process Design
        return [
          {
            title: 'Process Design',
            path: '/design/process/process-list',
            isActive: false,
          },
          {
            title: 'Edit Process Design',
            path: pathWithoutBase,
            isActive: true,
          },
        ];
      }
    }
    
    // Special case: For New Profile with ID (dynamic routes)
    if (pathWithoutBase.startsWith('/design/lib-profile/new-profile/')) {
      const segments = pathWithoutBase.split('/');
      const id = segments[segments.length - 1];
      
      if (id === '1') {
        // New Profile
        return [
          {
            title: 'Library Design',
            path: '/design/lib-profile/record-profile-design',
            isActive: false,
          },
          {
            title: 'Add new',
            path: pathWithoutBase,
            isActive: true,
          },
        ];
      } else {
        // Edit Profile
        return [
          {
            title: 'Library Design',
            path: '/design/lib-profile/record-profile-design',
            isActive: false,
          },
          {
            title: 'Edit Profile',
            path: pathWithoutBase,
            isActive: true,
          },
        ];
      }
    }
    if (pathWithoutBase.startsWith('/process/process-access')) {
      // Special case: For process access setting routes
      if (pathWithoutBase.includes('/process-access-permission')) {
      
        return [
          {
            title: 'Process Access Setting',
            path: pathWithoutBase,
            isActive: true,
          },
        ];
      }
      
      // Main process access page
      return [
        {
          title: 'Process',
          path: pathWithoutBase,
          isActive: true,
        },
      ];
    }
    
    // Split into segments
    const segments = pathWithoutBase.split('/').filter(Boolean);
    
    if (segments.length === 0) {
      return [];
    }
    
    // Build breadcrumb array by accumulating path segments
    const crumbs: PageLink[] = [];
    let currentPath = '';
    let lastStaticPath = '';
    
    const isDynamicSegment = (value: string) => {
      // Skip typical id/code segments (numeric or hex/uuid-like)
      return (/^[0-9]+$/).test(value) || (/^[0-9a-fA-F-]{6,}$/).test(value);
    };

    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === segments.length - 1;
      
      // If this looks like an id segment, skip it but track the path
      if (isDynamicSegment(segment)) {
        return;
      }

      // Try to get label from config with dynamic segment pattern
      let title = getBreadcrumbLabel(currentPath);
      
      // If no exact match and we have a partially formed path, try pattern matching
      // For routes like /process/process-access/:processCode/tasks/:transactionNo
      if (title === currentPath) {
        // Try to find a matching pattern in config
        if (currentPath.includes('/tasks')) {
          if (isLast) {
            title = getBreadcrumbLabel('/process/process-access/:processCode/tasks/:transactionNo') || 'Task Details';
          } else {
            title = getBreadcrumbLabel('/process/process-access/:processCode/tasks') || 'Task Board';
          }
        }
      }

      lastStaticPath = currentPath;
      
      crumbs.push({
        title,
        path: currentPath,
        isActive: isLast,
        // No isSeparator needed - CSS handles it!
      });
    });
    
    // Handle cases where the URL has dynamic segments at the end
    // e.g., /process/process-access/PROC123/tasks/TRANS456
    const lastSegment = segments[segments.length - 1];
    if (isDynamicSegment(lastSegment) && lastStaticPath.includes('/tasks')) {
      const finalLabel = getBreadcrumbLabel('/process/process-access/:processCode/tasks/:transactionNo') || 'Process';
      crumbs.push({
        title: finalLabel,
        path: currentPath,
        isActive: true,
      });
    }
    
    return crumbs;
  }, [location.pathname]);
  
  return breadcrumbs;
}
